/* ============================================================
   tante-review-badge.js
   Reformats the Judge.me preview badge on collection-grid and featured
   collection product cards to the 2026 design: a leading numeric rating
   ("4,7"), gold stars, and a parenthesised review count ("(4)").

   The injected pieces (.tante-jdgm-num / .tante-jdgm-count) are shown via
   tante-product-card.css, and the script only touches badges inside
   .collection-grid-section or .featured-collection-section (and never inside
   a quick-view modal), so PDP badges are left completely untouched.

   Class contract with the CSS:
     · tante-jdgm-empty  → product has no reviews; the row is collapsed.
     · tante-jdgm-ready  → a review row is shown (formatted OR raw); used
                           to anchor the review+price block to the bottom.
     · tante-jdgm-fmt    → the formatted "4,7 … (n)" was injected, so the
                           original Judge.me text is hidden.

   Robust against AJAX grid swaps (facets / sorting / infinite scroll)
   and late Judge.me hydration via a MutationObserver on the section.
   ============================================================ */
(function () {
  'use strict';

  var BADGE_SELECTOR = '.collection-grid-section .jdgm-prev-badge, .featured-collection-section .jdgm-prev-badge';

  function formatRating(raw) {
    var n = parseFloat(raw);
    if (!isFinite(n) || n <= 0) return null;
    try {
      return n.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
    } catch (e) {
      return n.toFixed(1).replace('.', ',');
    }
  }

  // Review count. Prefer the data attribute; otherwise read the badge text,
  // preferring a parenthesised number, then the LAST integer (the count never
  // leads in Judge.me text formats that also contain the rating). NaN = unknown.
  function getCount(badge) {
    var n = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
    if (!isNaN(n)) return n;

    var txt = badge.querySelector('.jdgm-prev-badge__text');
    var t = txt && (txt.textContent || '');
    if (t) {
      var paren = t.match(/\((\d[\d.,]*)\)/);
      if (paren) return parseInt(paren[1].replace(/[.,]/g, ''), 10);
      var all = t.match(/\d+/g);
      if (all && all.length) return parseInt(all[all.length - 1], 10);
    }
    return NaN;
  }

  function enhance(inner) {
    var outer = inner.closest('.jdgm-preview-badge');
    if (!outer) return;
    // Final states — never re-process.
    if (outer.classList.contains('tante-jdgm-fmt') ||
        outer.classList.contains('tante-jdgm-empty')) return;
    // Leave a PDP badge shown inside a quick-view modal opened from a card.
    if (inner.closest('.quick-view, .quick-view__content, quick-view-drawer')) return;

    var count = getCount(inner);

    // Not hydrated yet — leave it for a later mutation.
    if (isNaN(count)) return;

    // No reviews — collapse the row.
    if (count <= 0) {
      outer.classList.add('tante-jdgm-empty');
      return;
    }

    var rating = formatRating(
      inner.getAttribute('data-average-rating') || inner.getAttribute('data-rating')
    );

    if (rating) {
      if (!inner.querySelector('.tante-jdgm-num')) {
        var num = document.createElement('span');
        num.className = 'tante-jdgm-num';
        num.setAttribute('aria-hidden', 'true');
        num.textContent = rating;
        inner.insertBefore(num, inner.firstChild);
      }
      if (!inner.querySelector('.tante-jdgm-count')) {
        var cnt = document.createElement('span');
        cnt.className = 'tante-jdgm-count';
        cnt.setAttribute('aria-hidden', 'true');
        cnt.textContent = '(' + count + ')';
        inner.appendChild(cnt);
      }
      outer.classList.add('tante-jdgm-ready', 'tante-jdgm-fmt');
    } else {
      // Count is known but no rating value — keep Judge.me's own stars + text
      // (not finalised, so it can upgrade if a rating hydrates in later).
      outer.classList.add('tante-jdgm-ready');
    }
  }

  function enhanceAll() {
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    for (var i = 0; i < badges.length; i++) enhance(badges[i]);
  }

  function init() {
    enhanceAll();

    // Observe a stable ancestor. Collection grids can be swapped by AJAX and
    // featured badges can hydrate after the section itself is present.
    var container = document.body;

    if (container && 'MutationObserver' in window) {
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true; // coalesce bursts (e.g. an AJAX grid swap)
        window.requestAnimationFrame(function () {
          pending = false;
          enhanceAll();
        });
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-average-rating', 'data-number-of-reviews']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
